import type { EventSourceMessage } from '@microsoft/fetch-event-source'
import type { ApiResponse, ApiResponseEnvelope } from '@/shared/api/types/api-response'
import type {
  LocalizationJobBackendStatus,
  LocalizationJobCreateResponseData,
  LocalizationJobDTO,
  LocalizationJobStatusResponseData,
  LocalizedSpeechPreviewJobItemDTO,
  LocalizedSpeechPreviewJobRequest,
  LocalizedSpeechPreviewRequest,
  LocalizedSpeechPreviewResponseData,
} from '@/shared/api/types/localized-speech-types'
import { fetchEventSource } from '@microsoft/fetch-event-source'
import { GW } from '@/shared/api/endpoints'
import { canSendTenantHeader } from '@/shared/api/utils/tenant-header'
import { unwrapApiEnvelope } from '@/shared/api/utils/unwrapapi'
import { TRANSLATION_JOB_STATUS } from '@/shared/constants/status'
import { toCamelCaseKeys } from '@/shared/libs/case-transform'
import { getStoredTenantId, getStoredToken } from '@/shared/libs/get-auth-token'
import { apiLogger } from '@/shared/libs/logger'
import { refreshAccessToken, request } from '@/shared/libs/request'

const LOCALIZED_SPEECH_PREVIEW_JOBS_PATH = 'admin/localized-speech/preview/jobs'
const DEFAULT_POLL_INTERVAL_MS = 2000

function mapPreviewPayloadToJobRequest(
  payload: LocalizedSpeechPreviewRequest,
): LocalizedSpeechPreviewJobRequest {
  return {
    fields: payload.fields,
    input_language: payload.inputLanguage,
    output_languages: payload.outputLanguages,
    audio_enable: payload.audioEnabled,
    speech_field: payload.speechField ?? '',
  }
}

function mapJobData(
  data: LocalizationJobDTO['data'],
): LocalizedSpeechPreviewResponseData | undefined {
  if (!data) {
    return undefined
  }

  return Object.fromEntries(
    Object.entries(data).map(([language, value]) => {
      const audioUrl = value.speechUrl ?? value.audioUrl ?? undefined
      return [
        language,
        {
          fields: value.fields ?? {},
          ...(audioUrl ? { speechUrl: audioUrl, audioUrl } : {}),
        },
      ]
    }),
  )
}

function mapJobDtoToStatus(dto: LocalizationJobDTO): LocalizationJobStatusResponseData {
  const result: LocalizationJobStatusResponseData = {
    jobId: dto.jobId,
    status: dto.status,
  }
  const data = mapJobData(dto.data)
  if (data) {
    result.data = data
  }
  if (dto.error) {
    result.error = dto.error
  }
  return result
}

export async function createLocalizationJob(
  payload: LocalizedSpeechPreviewRequest,
): Promise<ApiResponse<LocalizationJobCreateResponseData>> {
  const response = await request.post<ApiResponseEnvelope<LocalizationJobDTO>>(
    GW.packaging(LOCALIZED_SPEECH_PREVIEW_JOBS_PATH),
    mapPreviewPayloadToJobRequest(payload),
  )
  const data = unwrapApiEnvelope(response.data)

  return {
    data: {
      jobId: data.jobId,
      status: data.status,
    },
    ...(response.data.message ? { message: response.data.message } : {}),
  }
}

export async function getLocalizationJob(
  jobId: string,
): Promise<ApiResponse<LocalizationJobStatusResponseData>> {
  const response = await request.get<ApiResponseEnvelope<LocalizationJobDTO>>(
    GW.packaging(`${LOCALIZED_SPEECH_PREVIEW_JOBS_PATH}/${jobId}`),
  )
  const data = unwrapApiEnvelope(response.data)

  return {
    data: mapJobDtoToStatus(data),
    ...(response.data.message ? { message: response.data.message } : {}),
  }
}

export async function deleteLocalizationJob(jobId: string): Promise<number> {
  const response = await request.delete(
    GW.packaging(`${LOCALIZED_SPEECH_PREVIEW_JOBS_PATH}/${jobId}`),
  )
  return response.status
}

export interface LocalizationJobStreamHandlers {
  onSnapshot: (
    data: LocalizedSpeechPreviewResponseData,
    status: LocalizationJobBackendStatus,
  ) => void
  onLanguageResult: (language: string, item: LocalizedSpeechPreviewJobItemDTO) => void
  onCompleted: (data: LocalizedSpeechPreviewResponseData) => void
  onFailed: (error: string) => void
  onCancelled: () => void
  onTimeout: () => void
  onStreamError: (error: unknown) => void
}

/** Thrown from onopen when the access token was rejected; triggers a single refresh-and-retry. */
class StreamAuthExpiredError extends Error {}
/** Thrown from onopen for any other non-2xx response (e.g. 404); stops retries immediately. */
export class StreamFatalError extends Error {
  constructor(public status: number) {
    super(`Localization job stream failed with status ${status}`)
  }
}

function parseSingleLanguageResult(
  payload: LocalizationJobStreamPayload,
): [string, LocalizedSpeechPreviewJobItemDTO] | undefined {
  const record = payload.data
  if (!record) {
    return undefined
  }

  const [recordLanguage, recordItem] = Object.entries(record)[0] ?? []
  const language = payload.language ?? recordLanguage
  const item = (language ? record[language] : undefined) ?? recordItem
  if (!language || !item) {
    return undefined
  }

  return [language, item] as [string, LocalizedSpeechPreviewJobItemDTO]
}

interface LocalizationJobStreamPayload {
  language?: string
  status?: LocalizationJobBackendStatus
  data?: LocalizationJobDTO['data']
  error?: string
}

function dispatchStreamMessage(
  ev: EventSourceMessage,
  handlers: LocalizationJobStreamHandlers,
  controller: AbortController,
) {
  if (!ev.data) {
    return
  }
  let payload: LocalizationJobStreamPayload
  try {
    payload = toCamelCaseKeys(JSON.parse(ev.data) as unknown) as LocalizationJobStreamPayload
  } catch (parseError) {
    handlers.onStreamError(parseError)
    controller.abort()
    return
  }

  switch (ev.event) {
    case 'snapshot':
      handlers.onSnapshot(
        mapJobData(payload.data) ?? {},
        payload.status ?? TRANSLATION_JOB_STATUS.PROCESSING,
      )
      break
    case 'language_result': {
      const entry = parseSingleLanguageResult(payload)
      if (entry) {
        handlers.onLanguageResult(entry[0], entry[1])
      }
      break
    }
    case 'completed':
      handlers.onCompleted(mapJobData(payload.data) ?? {})
      controller.abort()
      break
    case 'failed':
      handlers.onFailed(payload.error ?? '')
      controller.abort()
      break
    case 'cancelled':
      handlers.onCancelled()
      controller.abort()
      break
    case 'timeout':
      handlers.onTimeout()
      break
    default:
      break
  }
}

async function connectLocalizationJobStream(
  jobId: string,
  handlers: LocalizationJobStreamHandlers,
  controller: AbortController,
  isRetryAfterRefresh = false,
): Promise<void> {
  const token = getStoredToken()
  const tenantId = getStoredTenantId()
  const baseUrl = import.meta.env.VITE_API_URL.replace(/\/+$/, '')
  const streamPath = GW.packaging(`${LOCALIZED_SPEECH_PREVIEW_JOBS_PATH}/${jobId}/stream`).replace(
    /^\/+/,
    '',
  )
  const url = `${baseUrl}/${streamPath}`

  try {
    await fetchEventSource(url, {
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(tenantId && canSendTenantHeader() ? { 'X-Tenant-Id': tenantId } : {}),
      },
      signal: controller.signal,
      openWhenHidden: true,
      async onopen(response) {
        if (response.ok) {
          return
        }
        if (response.status === 401 && !isRetryAfterRefresh) {
          throw new StreamAuthExpiredError()
        }
        throw new StreamFatalError(response.status)
      },
      onmessage: (ev) => dispatchStreamMessage(ev, handlers, controller),
      onerror(error: unknown) {
        if (
          controller.signal.aborted ||
          error instanceof StreamAuthExpiredError ||
          error instanceof StreamFatalError
        ) {
          throw error
        }
        apiLogger.warn('Localization job stream transient error, retrying', { jobId, error })
      },
    })
  } catch (error) {
    if (controller.signal.aborted) {
      return
    }
    if (error instanceof StreamAuthExpiredError) {
      try {
        await refreshAccessToken()
      } catch (refreshError) {
        handlers.onStreamError(refreshError)
        return
      }
      await connectLocalizationJobStream(jobId, handlers, controller, true)
      return
    }
    handlers.onStreamError(error)
  }
}

/**
 * Opens an SSE connection tracking a localization job's progress.
 * Returns a cleanup function that aborts the stream.
 */
export function streamLocalizationJob(
  jobId: string,
  handlers: LocalizationJobStreamHandlers,
): () => void {
  const controller = new AbortController()
  void connectLocalizationJobStream(jobId, handlers, controller)
  return () => controller.abort()
}

export async function runLocalizationJobPreview(
  payload: LocalizedSpeechPreviewRequest,
  options?: {
    pollIntervalMs?: number
  },
): Promise<ApiResponse<LocalizedSpeechPreviewResponseData>> {
  const created = await createLocalizationJob(payload)
  const pollIntervalMs = options?.pollIntervalMs ?? DEFAULT_POLL_INTERVAL_MS

  while (true) {
    const status = await getLocalizationJob(created.data.jobId)
    if (status.data.status === TRANSLATION_JOB_STATUS.SUCCESS) {
      return { data: status.data.data ?? {} }
    }
    if (status.data.status === TRANSLATION_JOB_STATUS.FAILED) {
      throw new Error(status.data.error ?? 'Localization job failed')
    }
    if (status.data.status !== TRANSLATION_JOB_STATUS.PROCESSING) {
      throw new Error('Unsupported localization job status')
    }

    await new Promise<void>((resolve) => window.setTimeout(resolve, pollIntervalMs))
  }
}
